import { lazy } from 'react';

/**
 * @map componentMap
 * @description The authoritative bridge between JSON string IDs and React Code.
 *
 * ⚠️ RULES:
 * 1. The Key (e.g., "MainDashboard") must match the "componentId" in navigation.json
 *    or in `useDynamicCatalog.js`.
 * 2. All imports must be Lazy to ensure high performance (Standard 6).
 *
 * As of the legacy-cleanup commit, every catalog row that used to point at
 * the per-service single-screen `*Control` page now points at the new
 * 3-screen module's Home shell. The List/Detail entries below are wired
 * directly into routes.jsx (one route per entity per page) and are not
 * referenced by the catalog itself.
 */
export const componentMap = {
  // 🏛️ Identity / non-control surfaces (kept as-is)
  "MainDashboard": lazy(() => import('../modules/identity/pages/Dashboard')),
  "UserProfile":   lazy(() => import('../modules/identity/pages/Profile')),
  "IdentityAdmin": lazy(() => import('../modules/identity/pages/IdentityConsole')),
  "UsersAdminDashboard": lazy(() => import('../modules/identity/pages/UsersAdminDashboard')),
  "UsersAdminSettings":  lazy(() => import('../modules/identity/pages/UsersAdminSettings')),
  // OperatorConsole and CustomerHome both render the same unified Home
  // page; persona detection happens inside Home via `useRole()`. Routes
  // unchanged — just the component each lazy-loads.
  "OperatorConsole":     lazy(() => import('../modules/home/pages/Home.jsx')),
  "AuthControl":   lazy(() => import('../modules/identity/pages/AuthControl')),
  "PermissionsAdmin": lazy(() => import('../modules/permissions/pages/PermissionsManager')),
  "OrgManager":    lazy(() => import('../modules/identity/pages/OrgManager')),

  // 📦 Asset & Dictionary Domain
  "AssetControl":     lazy(() => import('../modules/resource-vault/pages/AssetHub')),
  "StorageControl":   lazy(() => import('../modules/resource-vault/pages/StorageControl')),

  // Projects / Requirements (existing)
  "ProjectList":          lazy(() => import('../modules/projects/pages/ProjectList')),
  "ProjectControl":       lazy(() => import('../modules/projects/pages/ProjectControl')),
  "PreviewControl":       lazy(() => import('../modules/projects/pages/PreviewControl')),
  "RequirementsAI":       lazy(() => import('../modules/requirements/pages/RequirementsAI')),
  "RequirementsControl":  lazy(() => import('../modules/requirements/pages/RequirementsControl')),

  // 💳 Billing / commerce (legacy single-screen pages still in use)
  "BillingSubscription": lazy(() => import('../modules/billing/pages/Subscription')),
  "BillingHub":          lazy(() => import('../modules/billing/pages/BillingHub')),
  "MyReviews":           lazy(() => import('../modules/requirements/pages/MyReviewsPage')),
  // Customer-side landing console — analogue of OperatorConsole for
  // users on the customer track. Mounted at /home; sits at the top of
  // the Workspace section in the sidebar.
  "CustomerHome":        lazy(() => import('../modules/home/pages/Home.jsx')),
  "BillingAdmin":        lazy(() => import('../modules/billing/pages/AdminBilling')),
  "AuthProviders":       lazy(() => import('../modules/admin-infra/pages/AuthProviders.jsx')),
  // Payment provider registry + Stripe configuration (read-only secrets).
  "PaymentSettings":     lazy(() => import('../modules/admin-infra/pages/PaymentSettings.jsx')),
  // Review marketplace policy — kill-switch, caps, section eligibility.
  "ReviewPolicy":        lazy(() => import('../modules/admin-infra/pages/ReviewPolicy.jsx')),

  // 🪜 Progression / MOX wallet (added in audit-closure pass)
  "MyLadder":    lazy(() => import('../modules/progression/pages/MyLadder.jsx')),
  "MyMoxWallet": lazy(() => import('../modules/progression/pages/MyMoxWallet.jsx')),

  // 📦 User-facing concierge (the existing ConciergeQueue is admin-only)
  "MyConciergeRequests": lazy(() => import('../modules/concierge/pages/MyConciergeRequests.jsx')),
  "ConciergeQueue":      lazy(() => import('../modules/concierge/pages/ConciergeQueue.jsx')),
  "ReviewerDashboard":   lazy(() => import('../modules/reviewer/pages/ReviewerDashboard.jsx')),
  "BillingPillars":      lazy(() => import('../modules/billing/pages/Pillars.jsx')),
  "PurchasesWorkspace":  lazy(() => import('../modules/billing/pages/PurchasesWorkspace.jsx')),

  // ─── 3-screen service modules ────────────────────────────────────────
  // Catalog rows reference the *Home id; the List and Detail ids are
  // wired into specific routes in routes.jsx (one entry per entity).
  "AuditHome":   lazy(() => import('../modules/audit/pages/AuditHome.jsx')),
  "AuditList":   lazy(() => import('../modules/audit/pages/AuditList.jsx')),
  "AuditDetail": lazy(() => import('../modules/audit/pages/AuditDetail.jsx')),

  "EventsHome":   lazy(() => import('../modules/events/pages/EventsHome.jsx')),
  "EventsList":   lazy(() => import('../modules/events/pages/EventsList.jsx')),
  "EventsDetail": lazy(() => import('../modules/events/pages/EventsDetail.jsx')),

  "NotificationsHome":      lazy(() => import('../modules/notifications/pages/NotificationsHome.jsx')),
  "NotificationsList":      lazy(() => import('../modules/notifications/pages/NotificationsList.jsx')),
  "NotificationsDetail":    lazy(() => import('../modules/notifications/pages/NotificationsDetail.jsx')),
  // Per-user notification preferences — listed in the Account sidebar
  // (not Operations) so users self-manage their channel + event settings.
  "NotificationPreferences": lazy(() => import('../modules/notifications/pages/NotificationPreferences.jsx')),

  "JobsHome":   lazy(() => import('../modules/jobs/pages/JobsHome.jsx')),
  "JobsList":   lazy(() => import('../modules/jobs/pages/JobsList.jsx')),
  "JobsDetail": lazy(() => import('../modules/jobs/pages/JobsDetail.jsx')),

  "OrchestratorHome":   lazy(() => import('../modules/orchestrator/pages/OrchestratorHome.jsx')),
  "OrchestratorList":   lazy(() => import('../modules/orchestrator/pages/OrchestratorList.jsx')),
  "OrchestratorDetail": lazy(() => import('../modules/orchestrator/pages/OrchestratorDetail.jsx')),

  "RegistryHome":   lazy(() => import('../modules/registry/pages/RegistryHome.jsx')),
  "RegistryList":   lazy(() => import('../modules/registry/pages/RegistryList.jsx')),
  "RegistryDetail": lazy(() => import('../modules/registry/pages/RegistryDetail.jsx')),

  "CommunicationsHome":   lazy(() => import('../modules/communications/pages/CommunicationsHome.jsx')),
  "CommunicationsList":   lazy(() => import('../modules/communications/pages/CommunicationsList.jsx')),
  "CommunicationsDetail": lazy(() => import('../modules/communications/pages/CommunicationsDetail.jsx')),

  // Payments module — Overview is the admin landing; the others are
  // sub-surfaces that share a tab nav inside PageShell.
  "PaymentsHome":         lazy(() => import('../modules/payments/pages/PaymentsHome.jsx')),
  "PaymentsList":         lazy(() => import('../modules/payments/pages/PaymentsList.jsx')),
  "PaymentsDetail":       lazy(() => import('../modules/payments/pages/PaymentsDetail.jsx')),
  "PaymentsTransactions": lazy(() => import('../modules/payments/pages/PaymentsTransactions.jsx')),
  "PaymentsDisputes":     lazy(() => import('../modules/payments/pages/PaymentsDisputes.jsx')),
  "PaymentsProviders":    lazy(() => import('../modules/payments/pages/PaymentsProviders.jsx')),

  "SubscriptionsHome":   lazy(() => import('../modules/subscriptions/pages/SubscriptionsHome.jsx')),
  "SubscriptionsList":   lazy(() => import('../modules/subscriptions/pages/SubscriptionsList.jsx')),
  "SubscriptionsDetail": lazy(() => import('../modules/subscriptions/pages/SubscriptionsDetail.jsx')),

  "DownloadsHome":   lazy(() => import('../modules/downloads/pages/DownloadsHome.jsx')),
  "DownloadsList":   lazy(() => import('../modules/downloads/pages/DownloadsList.jsx')),
  "DownloadsDetail": lazy(() => import('../modules/downloads/pages/DownloadsDetail.jsx')),

  "StorageHome":   lazy(() => import('../modules/storage/pages/StorageHome.jsx')),
  "StorageList":   lazy(() => import('../modules/storage/pages/StorageList.jsx')),
  "StorageDetail": lazy(() => import('../modules/storage/pages/StorageDetail.jsx')),

  "AiLlmHome":   lazy(() => import('../modules/ai-llm/pages/AiLlmHome.jsx')),
  "AiLlmList":   lazy(() => import('../modules/ai-llm/pages/AiLlmList.jsx')),
  "AiLlmDetail": lazy(() => import('../modules/ai-llm/pages/AiLlmDetail.jsx')),

  "AiMemoryHome":   lazy(() => import('../modules/ai-memory/pages/AiMemoryHome.jsx')),
  "AiMemoryList":   lazy(() => import('../modules/ai-memory/pages/AiMemoryList.jsx')),
  "AiMemoryDetail": lazy(() => import('../modules/ai-memory/pages/AiMemoryDetail.jsx')),

  "AiPolicyHome":   lazy(() => import('../modules/ai-policy/pages/AiPolicyHome.jsx')),
  "AiPolicyList":   lazy(() => import('../modules/ai-policy/pages/AiPolicyList.jsx')),
  "AiPolicyDetail": lazy(() => import('../modules/ai-policy/pages/AiPolicyDetail.jsx')),

  // Gateway monitoring is its own surface — no 3-screen replacement.
  "GatewayControl": lazy(() => import('../modules/admin-infra/pages/GatewayControl')),

  // 🏗️ Infrastructure Domain (Service #32, #35)
  "InfraMonitor":  lazy(() => import('../modules/admin-infra/pages/InfraDashboard')),

  // 🏛️ Operator console (priority #5) — owner-group governance pages.
  // Backend-bound to priorities #2 (eligibility matrix), #3 (sectional
  // review), and forthcoming PRs for earnings + scoring weights.
  "EligibilityMatrix":  lazy(() => import('../modules/admin-infra/pages/EligibilityMatrix')),
  "ReviewerEarnings":   lazy(() => import('../modules/admin-infra/pages/ReviewerEarnings')),
  "CommonsPage":        lazy(() => import('../modules/commons/pages/CommonsPage.jsx')),
  "CommonsWindowPage":  lazy(() => import('../modules/commons/pages/CommonsWindowPage.jsx')),
  "CommonsMePage":      lazy(() => import('../modules/commons/pages/CommonsMePage.jsx')),
  "CommonsConsole":     lazy(() => import('../modules/commons/pages/CommonsConsole.jsx')),
  "CommonsGovernancePage": lazy(() => import('../modules/commons/pages/CommonsGovernancePage.jsx')),
  "ScoringWeights":     lazy(() => import('../modules/admin-infra/pages/ScoringWeights')),
  "ReportManager":      lazy(() => import('../modules/reports-manager/pages/ReportManagerPage.jsx')),
};
